// A league's scoring as a table anyone can read (PLAN-2026-10-18-scoring-
// anyone-can-read §2.2, §2.6): one column, a section per position, one value
// a row, read top to bottom. The create form's step, League settings and the
// rules page all draw it, from the `ScoringTable` the domain builds — the
// rows, their words and the usual numbers are the backend's; how they are
// laid out is this file's.
//
// READING: position chips over the table (a rail on wider screens) narrow it
// to one position; a search finds a rule by its words or by the names
// experienced players use. EDITING (the create form's Change any number and
// the commissioner's tool): every row is a button that opens the editor, and
// the zeros the sport offers sit under "More you can score".

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ScoringGroup, ScoringRow, ScoringSection, ScoringTable } from '@fsp/domain';
import { Icon } from '../../kit/Icon';
import { Button, PinnedTop, Stack, TextField } from '../../kit/ui';
import { useTier } from '../../shell/useTier';
import '../../styles/screens/scoring.css';

/** "−2", "1.5", a bonus "+3". */
export function pointsText(points: number, bonus = false): string {
  const abs = Math.abs(points);
  const n = Number.isInteger(abs) ? String(abs) : String(Math.round(abs * 100) / 100);
  if (points < 0) return `−${n}`;
  return bonus ? `+${n}` : n;
}

function matches(row: ScoringRow, tokens: readonly string[]): boolean {
  const said = row.words.join(' ');
  return tokens.every((t) => said.includes(t));
}

export function ScoringTableView({
  table,
  editing = false,
  searchable = false,
  inline = false,
  onPick,
  canAddBonus,
  onAddBonus,
}: {
  table: ScoringTable;
  editing?: boolean;
  searchable?: boolean;
  /**
   * Inside a page with its own rail and sticky chips (the rules page): the
   * positions as chips at the top of the table, never a second rail, held at
   * the top with the search while the scoring scrolls (B-486).
   */
  inline?: boolean;
  /** Editing: a row was chosen. */
  onPick?: (row: ScoringRow, section: ScoringSection) => void;
  /** Editing: whether a kind of play can take a big-game bonus. */
  canAddBonus?: (groupId: string) => boolean;
  onAddBonus?: (section: ScoringSection, group: ScoringGroup) => void;
}) {
  const wide = useTier() !== 'phone' && !inline;
  const [only, setOnly] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const tokens = useMemo(() => query.trim().toLowerCase().split(/\s+/).filter(Boolean), [query]);

  // THE ROWS A CHANGE MOVED flash once (not under reduced motion): a switch
  // on the create form, or a number in the editor, shows where it landed.
  const before = useRef<Map<string, number> | null>(null);
  const moved = useMemo(() => {
    const out = new Set<string>();
    const prior = before.current;
    if (prior === null) return out;
    for (const s of table.sections)
      for (const g of [...s.groups, ...s.more])
        for (const r of g.rows) {
          const k = `${s.position}|${r.id}`;
          const was = prior.get(k);
          if (was !== undefined && was !== r.points) out.add(k);
        }
    return out;
  }, [table]);
  useEffect(() => {
    const now = new Map<string, number>();
    for (const s of table.sections)
      for (const g of [...s.groups, ...s.more])
        for (const r of g.rows) now.set(`${s.position}|${r.id}`, r.points);
    before.current = now;
  }, [table]);

  const sections = table.sections.filter((s) => only === null || s.position === only);
  const searching = tokens.length > 0;

  const rowView = (row: ScoringRow, section: ScoringSection) => {
    const k = `${section.position}|${row.id}`;
    const value = (
      <>
        <span className="fsp-score-label">{row.label}</span>
        <span className="fsp-score-value fsp-num">
          {pointsText(row.points, row.kind === 'bonus')}
          {row.stacked ? <span className="fsp-score-more-word"> more</span> : null}
        </span>
        {row.usual !== null ? (
          <span className="fsp-score-usual">
            usually {pointsText(row.usual, row.kind === 'bonus')}
          </span>
        ) : null}
      </>
    );
    const cls = `fsp-score-row${row.usual !== null ? ' fsp-score-changed' : ''}${moved.has(k) ? ' fsp-score-moved' : ''}`;
    // The key carries the value, so a second change flashes again.
    const key = `${row.id}:${row.points}`;
    if (!editing) {
      return (
        <div key={key} className={cls}>
          {value}
        </div>
      );
    }
    return (
      <button
        key={key}
        type="button"
        className={`${cls} fsp-score-edit`}
        onClick={() => onPick?.(row, section)}
        aria-label={`${row.label}: ${pointsText(row.points, row.kind === 'bonus')}. Change it`}
      >
        {value}
        <Icon name="edit" size="sm" />
      </button>
    );
  };

  const groupView = (group: ScoringGroup, section: ScoringSection, titled: boolean) => {
    const rows = searching ? group.rows.filter((r) => matches(r, tokens)) : group.rows;
    if (rows.length === 0) return null;
    const regular = rows.filter((r) => !r.offered);
    const offered = rows.filter((r) => r.offered);
    return (
      <div key={group.id} className="fsp-score-group">
        {titled ? <div className="fsp-score-group-title">{group.title}</div> : null}
        {regular.map((r) => rowView(r, section))}
        {offered.length > 0 ? (
          <>
            <div className="fsp-score-offered">More you can score</div>
            {offered.map((r) => rowView(r, section))}
          </>
        ) : null}
        {editing && !searching && canAddBonus?.(group.id) && onAddBonus ? (
          <div className="fsp-score-add">
            <Button size="xs" variant="quiet" icon="add" onClick={() => onAddBonus(section, group)}>
              Add a big-game bonus
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  const sectionView = (section: ScoringSection) => {
    const open = searching || only === section.position || opened.has(section.position);
    const main = section.groups.map((g) => groupView(g, section, section.groups.length > 1));
    const more = open ? section.more.map((g) => groupView(g, section, true)) : [];
    const shown = [...main, ...more].filter(Boolean);
    if (searching && shown.length === 0) return null;
    return (
      <section key={section.position} className="fsp-score-section" aria-label={section.title}>
        <h3 className="fsp-score-title">{section.title}</h3>
        {shown}
        {!open && section.more.length > 0 ? (
          <button
            type="button"
            className="fsp-score-more"
            aria-expanded={false}
            onClick={() => setOpened((o) => new Set([...o, section.position]))}
          >
            More for {section.title.toLowerCase()} (
            {section.more.map((g) => g.title.toLowerCase()).join(', ')})
            <Icon name="next" size="sm" />
          </button>
        ) : null}
      </section>
    );
  };

  const picker = (
    <nav
      className={
        wide ? 'fsp-score-rail' : `fsp-score-chips${inline ? ' fsp-score-chips-inline' : ''}`
      }
      aria-label="Show the scoring for"
    >
      {[{ position: null as string | null, label: 'All' }]
        .concat(table.sections.map((s) => ({ position: s.position, label: s.position })))
        .map((p) => (
          <button
            key={p.position ?? 'all'}
            type="button"
            className="fsp-score-pick"
            aria-pressed={only === p.position}
            title={
              p.position === null
                ? 'Every position'
                : table.sections.find((s) => s.position === p.position)?.title
            }
            onClick={() => setOnly(p.position)}
          >
            {wide && p.position !== null
              ? table.sections.find((s) => s.position === p.position)?.title
              : p.label}
          </button>
        ))}
    </nav>
  );

  const body = sections.map(sectionView).filter(Boolean);

  const search = searchable ? (
    <TextField
      label="Find a rule"
      hideLabel
      value={query}
      onChange={setQuery}
      placeholder="Find a rule: catch, sack, field goal"
    />
  ) : null;

  // ON THE RULES PAGE THE CHIPS AND THE SEARCH STAY AT THE TOP OF THE SCORING
  // while its rules scroll under them (B-486): the scoring runs four screens,
  // and a position was a scroll back up away. One block, under the page's
  // own sticky row on a phone.
  if (inline)
    return (
      <div className="fsp-score">
        <PinnedTop className="fsp-score-tools">
          <Stack gap={2}>
            {picker}
            {search}
          </Stack>
        </PinnedTop>
        {body.length > 0 ? (
          body
        ) : (
          <p className="fsp-score-none">No rule mentions “{query.trim()}”.</p>
        )}
      </div>
    );

  return (
    <div className={`fsp-score${wide ? ' fsp-score-wide' : ''}`}>
      {picker}
      <div className="fsp-score-body">
        {search}
        {body.length > 0 ? (
          body
        ) : (
          <p className="fsp-score-none">No rule mentions “{query.trim()}”.</p>
        )}
      </div>
    </div>
  );
}
